'use client';

import Link from 'next/link';
import CountryFAQ from '@/components/country/CountryFAQ';
import GfSectionMark from '@/components/common/GfSectionMark';
import AffiliateLink from '@/components/common/AffiliateLink';
import GfArticleCards from '@/components/common/GfArticleCards';
import GfSubnav from '@/components/common/GfSubnav';
import GfVerticalHero from '@/components/common/GfVerticalHero';
import GfSteps from '@/components/common/GfSteps';
import GfCta from '@/components/common/GfCta';
import GfProviderCard from '@/components/common/GfProviderCard';
import GfPromoCodes from '@/components/country/GfPromoCodes';
import { ICONS, EMOJI_ICON } from '@/components/common/gfIcons';
import { makeCountryDisplay } from '@/lib/content/declension';
import { t } from '@/i18n/translations';

export default function VpnPageContent({ content, lang = 'ru', vpnArticles = [], promotions = [], background, country, countryName, localNotes = null }) {
    const isRu = lang === 'ru';
    const { cName, cCase } = makeCountryDisplay(countryName, country, isRu);
    const sp = t(lang, 'servicePage');   // общие тексты сервис-страниц
    const vp = t(lang, 'vpnPage');        // специфика VPN

    const whyCards = Array.isArray(content.whyCards) ? content.whyCards : [];
    const benefits = Array.isArray(content.benefits) ? content.benefits : [];
    const instructions = Array.isArray(content.instructions) ? content.instructions : [];
    const faqItems = (Array.isArray(content.faq) ? content.faq : []).map((it) => ({ question: it.q ?? it.question, answer: it.a ?? it.answer }));

    const whyTitle = content.whyTitle || vp.whyTitle;
    const benefitsTitle = content.benefitsTitle || vp.benefitsTitle;
    const instructionTitle = content.instructionTitle || vp.instructionTitle;

    // Provider list: real provider cards (internal) or recommended VPNs (affiliate).
    const buildDetail = (code) => (country ? `/${lang}/${country}/vpn/${code || ''}` : `/${lang}/montenegro/vpn/${code || ''}`);
    const providerList = (Array.isArray(content.providers) && content.providers.length)
        ? content.providers.map((p) => ({ key: p.slug, name: p.name, logo: p.logo, desc: p.description, href: `/${lang}/${country}/vpn/${p.slug}`, site: p.site || null, providerCode: p.slug }))
        : (Array.isArray(content.recommendedVpns) ? content.recommendedVpns : []).map((v, i) => ({
            key: v.providerCode || i, name: v.name, logo: v.logo, desc: v.description, providerCode: v.providerCode,
            href: v.detailsPath || buildDetail(v.providerCode), site: v.site && v.site !== '#' ? v.site : null,
        }));

    const has = {
        why: whyCards.length > 0,
        benefits: benefits.length > 0,
        local: Boolean(localNotes),
        providers: providerList.length > 0,
        how: instructions.length > 0,
        promos: promotions?.length > 0,
        faq: faqItems.length > 0,
        articles: vpnArticles?.length > 0,
    };


    const anchors = [
        (has.why || has.benefits) && { href: '#features', label: vp.aboutAnchor },
        has.local && { href: '#local', label: localNotes.eyebrow },
        has.providers && { href: '#providers', label: vp.servicesAnchor },
        has.how && { href: '#how', label: vp.heroHow },
        has.promos && { href: '#promocodes', label: sp.anchor.discounts },
        has.faq && { href: '#faq', label: sp.anchor.faq },
        has.articles && { href: '#articles', label: sp.anchor.articles },
    ].filter(Boolean);

    return (
        <div className="gf-root">
            {/* ── HERO ─────────────────────────────────────────── */}
            <GfVerticalHero background={background} title={content.title} sub={content.description}>
                {has.providers && <a className="pri" href="#providers">{vp.heroChoose} <b>→</b></a>}
                {has.how && <a className="sec" href="#how">{vp.heroHow}</a>}
            </GfVerticalHero>

            {/* ── SUBNAV ───────────────────────────────────────── */}
            <GfSubnav lang={lang} leaf={vp.leaf} anchors={anchors}
                crumbs={[cName && { href: `/${lang}/${country}`, label: cName }]} />

            {/* ── 01 · WHY VPN ─────────────────────────────────── */}
            {has.why && (
                <section className="gf-sec" id="features">
                    <GfSectionMark eyebrow={vp.whyEyebrow} title={whyTitle} />
                    {content.whySubtitle && <p className="gf-lead">{content.whySubtitle}</p>}
                    <div className="gf-scen gf-scen-feat cols-2">
                        {whyCards.map((card, i) => (
                            <article className="sc" key={i}>
                                <div className="ri-row">{ICONS[EMOJI_ICON[card.icon]] || ICONS.shield}<h3>{card.title}</h3></div>
                                {card.subtitle && <p>{card.subtitle}</p>}
                            </article>
                        ))}
                    </div>
                </section>
            )}

            {/* ── 02 · BENEFITS ────────────────────────────────── */}
            {has.benefits && (
                <section className="gf-sec" style={{ paddingTop: has.why ? 0 : undefined }}>
                    <GfSectionMark eyebrow={sp.eyebrow.benefits} title={benefitsTitle} />
                    <div className="gf-scen gf-scen-feat cols-2">
                        {benefits.map((b, i) => (
                            <article className="sc" key={i}>
                                <div className="ri-row">{ICONS[EMOJI_ICON[b.icon]] || ICONS.zap}<h3>{b.title}</h3></div>
                                {b.desc && <p>{b.desc}</p>}
                            </article>
                        ))}
                    </div>
                </section>
            )}

            {/* ── 02b · LOCAL ACCESS (country-unique) ──────────── */}
            {has.local && (
                <section className="gf-sec gf-localnet" id="local" style={{ paddingTop: (has.why || has.benefits) ? 0 : undefined }}>
                    <GfSectionMark eyebrow={localNotes.eyebrow} tag={cName || ''} title={localNotes.heading} />
                    {localNotes.intro && <p className="gf-lead">{localNotes.intro}</p>}
                    {localNotes.points.length > 0 && (
                        <ul className="gf-localnet-list">
                            {localNotes.points.map((point, i) => (
                                <li key={i}>{point}</li>
                            ))}
                        </ul>
                    )}
                    {localNotes.pricing && <p className="gf-localnet-pricing">{localNotes.pricing}</p>}
                    {localNotes.outro && <p className="gf-localnet-outro">{localNotes.outro}</p>}
                </section>
            )}

            {/* ── 03 · PROVIDERS ───────────────────────────────── */}
            {has.providers && (
                <section className="gf-sec" id="providers" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={vp.servicesEyebrow} tag={providerList.length} title={vp.providersTitle} />
                    {content.providersSubtitle && <p className="gf-lead">{content.providersSubtitle}</p>}
                    <div className="gf-grid gf-grid-center">
                        {providerList.map((p) => (
                            <GfProviderCard key={p.key} name={p.name} logo={p.logo} description={p.desc} className="pc-whole">
                                {/* партнёрская кнопка + карточка целиком ведёт в «Подробнее»: на хабе не было ни одной
                                    партнёрской ссылки, а цель «Подробнее» — 80×17 px (обход 01.10.2026) */}
                                <div className="gf-prov-actions">
                                    {p.site && (
                                        <AffiliateLink href={p.site} provider={p.providerCode} vertical="vpn" country={country} position="listing_card" className="buy">
                                            {vp.visit}
                                        </AffiliateLink>
                                    )}
                                    <Link className="visit" href={p.href}>{sp.details}</Link>
                                </div>
                            </GfProviderCard>
                        ))}
                    </div>
                </section>
            )}

            {/* ── 05 · HOW IT WORKS ────────────────────────────── */}
            {has.how && (
                <section className="gf-sec" id="how" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={sp.eyebrow.guide} tag={`${instructions.length} ${sp.stepsWord(instructions.length)}`} title={instructionTitle} />
                    {content.instructionSubtitle && <p className="gf-lead">{content.instructionSubtitle}</p>}
                    <GfSteps steps={instructions} />
                </section>
            )}

            {/* ── 06 · PROMO CODES ─────────────────────────────── */}
            {has.promos && (
                <section className="gf-sec" id="promocodes" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={sp.eyebrow.discounts} tag={sp.tag.current} title={vp.promosTitle} />
                    <p className="gf-lead">{vp.promoLead}</p>
                    <GfPromoCodes promotions={promotions} lang={lang} country={country} />
                </section>
            )}

            {/* ── 07 · FAQ ─────────────────────────────────────── */}
            {has.faq && (
                <section className="gf-sec" id="faq" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={sp.eyebrow.qa} tag={sp.tag.faq} title={sp.popularQuestions} />
                    {content.faqSubtitle && <p className="gf-lead">{content.faqSubtitle}</p>}
                    <CountryFAQ faqItems={faqItems} lang={lang} variant="gf" />
                    {content.faqLink?.href && content.faqLink?.text && (
                        <div style={{ marginTop: 26 }}><Link className="gf-alllink" href={content.faqLink.href}>{content.faqLink.text}</Link></div>
                    )}
                </section>
            )}

            {/* ── 06 · ARTICLES ────────────────────────────────── */}
            {has.articles && (
                <section className="gf-sec" id="articles" style={{ paddingTop: 0 }}>
                    <GfSectionMark eyebrow={sp.eyebrow.resources} tag={sp.tag.blog} title={vp.articlesTitle} allHref={`/${lang}/blog/category/vpn`} allLabel={sp.allArticles} />
                    <p className="gf-lead">{vp.articlesLead}</p>
                    {/* 10 карточек занимали 28% страницы Катара, 6 из них общие (обход 01.10.2026) — остальное по ссылке «Все статьи» */}
                    <GfArticleCards posts={vpnArticles.slice(0, 6)} lang={lang} variant="bento" bucket />
                </section>
            )}

            {/* ── CTA ──────────────────────────────────────────── */}
            <GfCta
                eyebrow={vp.ctaEyebrow}
                title={vp.ctaTitle(cName ? (isRu ? cCase('prepositional') : cName) : '')}
            >
                {has.providers && <a className="gf-btn" href="#providers">{vp.ctaBtn}</a>}
            </GfCta>
        </div>
    );
}
